import React, {
    Dispatch,
    ReactElement,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import {
    AddDocumentInput,
    EditDocumentInput,
    GetUploadUrlInput,
    ListDocumentsInput
} from '@amzn/coral_com-amazon-musicpaperrecordkeeperservice';
import errorTokens from '@amzn/meridian-tokens/base/icon/alert';
import doneTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import retryTokens from '@amzn/meridian-tokens/base/icon/refresh';
import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { RECORD_KEEPER_TENANT_ID, recordKeeperClient } from 'src/api/recordKeeper/RecordKeeperClient';
import { AppContext } from 'src/contexts/appContext';
import { formatBytes } from 'src/util/format-bytes/format-bytes';

export type FileStatus = 'ready' | 'uploading' | 'upload_error' | 'error' | 'done';

async function uploadFile(name: string, username: string, file: File, links?: { [key: string]: string }) {
    // get upload url
    const { presignedUrl } = await recordKeeperClient
        .getUploadUrl(
            GetUploadUrlInput.clone({
                tenantId: RECORD_KEEPER_TENANT_ID,
                documentName: name
            })
        )
        .toPromise();

    // upload to url
    await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/pdf'
        },
        body: file
    });

    // add document
    await recordKeeperClient
        .addDocument(
            AddDocumentInput.clone({
                tenantId: RECORD_KEEPER_TENANT_ID,
                username: username,
                documentName: name
            })
        )
        .toPromise();

    // update links if provided
    if (links && Object.keys(links).length > 0) {
        const { documents } = await recordKeeperClient
            .listDocuments(
                ListDocumentsInput.clone({
                    tenantId: RECORD_KEEPER_TENANT_ID
                })
            )
            .toPromise();

        const document = documents.find((d) => d.name === name);
        if (document) {
            await recordKeeperClient
                .editDocument(
                    EditDocumentInput.clone({
                        tenantId: RECORD_KEEPER_TENANT_ID,
                        documentId: document.documentId,
                        links
                    })
                )
                .toPromise();
        }
    }
}

interface IFileRow {
    id: number;
    file: File;
    width: number;
    startUpload: boolean;
    setReady: Dispatch<SetStateAction<Map<number, FileStatus>>>;
    onRemove: () => void;
}

export const FileRow: React.FC<IFileRow> = (props) => {
    const { id, file, startUpload, onRemove, setReady } = props;

    const { userInfo } = useContext(AppContext);

    const [name, setName] = useState(file.name);
    const [contractCentralLink, setContractCentralLink] = useState('');
    const [approvalsLink, setApprovalsLink] = useState('');
    const isValid = useMemo(() => file.name.endsWith('.pdf'), [file.name]);
    const [status, setStatus] = useState<FileStatus>(isValid ? 'ready' : 'error');
    const [statusText, setStatusText] = useState(isValid ? '' : 'File must be a .pdf');

    useEffect(() => {
        setReady((prev) => {
            const newValue = new Map(prev);
            newValue.set(id, status);
            return newValue;
        });
    }, [status, id]);

    const doUpload = useCallback(() => {
        setStatus('uploading');
        const links = {
            ...(contractCentralLink && { 'Contract Central': contractCentralLink }),
            ...(approvalsLink && { Approvals: approvalsLink })
        };
        uploadFile(name, userInfo.username, file, Object.keys(links).length > 0 ? links : undefined)
            .then(() => setStatus('done'))
            .catch((e) => {
                setStatus('upload_error');
                setStatusText(JSON.stringify(e.message ?? e));
            });
    }, [name, userInfo.username, contractCentralLink, approvalsLink, file]);

    useEffect(() => {
        if (startUpload && status === 'ready') {
            doUpload();
        }
    }, [startUpload, isValid]);

    return (
        <TableRow>
            <TableCell>
                <Row alignmentHorizontal={'end'}>{getStatusComponent(status, statusText, doUpload, onRemove)}</Row>
            </TableCell>
            <TableCell>
                <Input value={name} onChange={setName} width={`${props.width}em`} />
            </TableCell>
            <TableCell>{formatBytes(file.size)}</TableCell>
            <TableCell>
                <Input
                    value={contractCentralLink}
                    onChange={setContractCentralLink}
                    placeholder="Contract Central URL"
                    width="20em"
                />
            </TableCell>
            <TableCell>
                <Input value={approvalsLink} onChange={setApprovalsLink} placeholder="Approvals URL" width="20em" />
            </TableCell>
        </TableRow>
    );
};

function getStatusComponent(
    status: FileStatus,
    statusText: string,
    onRetry: () => void,
    onRemove: () => void
): ReactElement {
    const deleteButton = (
        <Tooltip position={'start'} title={'Remove'}>
            <Button onClick={onRemove} type={'link'} aria-label={'remove'}>
                <Text color={'secondary'}>
                    <Icon tokens={deleteTokens} />
                </Text>
            </Button>
        </Tooltip>
    );

    // using a button for consistent appearance and spacing
    const warningIcon = (
        <Tooltip position={'start'} title={statusText}>
            <Button type={'link'} aria-label={'alert'}>
                <Text color={'error'}>
                    <Icon tokens={errorTokens} />
                </Text>
            </Button>
        </Tooltip>
    );

    if (status === 'ready') {
        return deleteButton;
    } else if (status === 'uploading') {
        return <Loader size={'medium'} />;
    } else if (status === 'done') {
        return (
            <Button type={'link'} aria-label={'done'}>
                <Icon tokens={doneTokens} />
            </Button>
        );
    } else if (status === 'upload_error') {
        return (
            <Row>
                <Tooltip position={'start'} title={'Retry'}>
                    <Button onClick={onRetry} type={'link'} aria-label={'retry'}>
                        <Icon tokens={retryTokens} />
                    </Button>
                </Tooltip>
                {warningIcon}
                {deleteButton}
            </Row>
        );
    } else {
        return (
            <Row>
                {warningIcon}
                {deleteButton}
            </Row>
        );
    }
}
